import React from "react";
import styled from "styled-components";
import {
  Header as TemplateHeader,
  SubHeading as TemplateSubHeading,
  Description as TemplateDescription,
} from "../components/Typography/typography.styled";
import Footer from "../components/footers/MiniCenteredFooter";
import AnimationRevealPage from "../helpers/AnimationRevealPage";
import { MdOutlineSubdirectoryArrowRight } from "react-icons/md";

// Images
import daybreakMockup from "../images/daybreak-mockup.png";
import userStudyOne from "../images/User-Study-1.png";
import userStudyTwo from "../images/User-Study-2.png";
import colorPalatte from "../images/User-Study-3.png";
import flowDiagram from "../images/User-Study-4.png";
import userStudyFive from "../images/User-Study-5.png";
import userStudySix from "../images/User-Study-6.png";
import userStudySeven from "../images/User-Study-7.png";

const Header = styled(TemplateHeader)`
  margin-top: 1rem;
  font-size: 1.875rem;
  line-height: 2.25rem;
  line-height: 2.5rem;
  font-weight: 900;
  letter-spacing: 0.025em;
  line-height: 1.25;
  text-align: left;
  text-align: center;
  color: rgb(36, 62, 99);

  @media (min-width: 640px) {
    font-size: 2.25rem;
    line-height: 2.5rem;
    line-height: 1;
  }
  @media (min-width: 768px) {
    text-align: left;
  }
  @media (min-width: 1024px) {
    font-size: 3rem;
    line-height: 3.5rem;
  }
`;

const Subheading = styled(TemplateSubHeading)`
  text-align: center;
  text-transform: uppercase;
  color: #6415ff;
  letter-spacing: 0.1rem;
  font-size: 1rem;
  margin-top: 4rem;

  @media (min-width: 768px) {
    text-align: left;
    margin-top: 0rem;
  }
`;

const PrimaryHeaderWithLine = ({ title = "" }) => {
  return <h3 className="pb-4 mb-4 fst-italic border-bottom">{title}</h3>;
};

const SubHeaderWithLineAndArrow = ({
  description = "The start of something 👷‍♂️",
}) => {
  return (
    <h4 className="d-flex">
      <MdOutlineSubdirectoryArrowRight />
      <span className="ms-2"> {description}</span>
    </h4>
  );
};

const SubHeaderDescription = ({ paragraph = "" }) => {
  return <p className="mb-4">{paragraph}</p>;
};

const Daybreak = () => {
  return (
    <AnimationRevealPage>
      <div className="container col-xxl-8 px-4 py-5">
        <div className="row flex-lg-row-reverse align-items-center g-5 py-5">
          <div className="col-10 col-sm-8 col-lg-6">
            <img
              src={daybreakMockup}
              className="d-block mx-lg-auto img-fluid"
              loading="lazy"
            />
          </div>
          <div className="col-lg-6">
            <Subheading>Mobile App - 5 minutes read</Subheading>
            <Header className="display-5 fw-bold lh-1 mb-3">
              The Thoughts, Motivations, and Effort behind Daybreak, the App
            </Header>
            <p className="lead">
              Daybreak is the meditation app that helps you stay mindful of your
              thoughts, and helps you stay motivated to achieve your goals. But
              have you ever thought of the process of creating a meditation app?
            </p>
          </div>
        </div>
        <div className="row g-5 my-3">
          <div className="col-md-12">
            <div className="section ideation">
              <PrimaryHeaderWithLine title={`1. Ideation...`} />

              <SubHeaderDescription
                paragraph={
                  <>
                    After considering several options, such as a medication app
                    or a study social media app, our team settled on this
                    meditation app. The main reason for this choice was because
                    this entire concept resonated with every team member, as we
                    realized that many of us struggled with staying focused and
                    maintaining high efficiency when doing our work.
                    <br />
                    <br />
                    We discovered that we were not the only ones affected by
                    this problem after conducting additional research. According
                    to a report,{" "}
                    <a href="https://www.insidehighered.com/digital-learning/article/2019/07/10/survey-shows-nearly-half-students-distracted-technology">
                      49% of 478 students and 36 instructors{" "}
                    </a>
                    said that using technology outside of work or school was
                    distracting. This enabled us to tailor our application to
                    assist users in focusing more effectively. This was where we
                    decided to create this meditation app.
                  </>
                }
              />
            </div>

            <br />

            <div className="section getting-started">
              {/** Title */}
              <PrimaryHeaderWithLine title={`2. The start of something`} />

              {/** User studies */}
              <div>
                <SubHeaderWithLineAndArrow
                  description={"2.1. Conducting User studies 🧔"}
                />
                <SubHeaderDescription
                  paragraph={`To get things started, We conducted user studies to better
                  understand the problems that our app was attempting to solve
                  in order to help us better understand the problems that we
                  were attempting to solve. To better understand the user's pain
                  points and problems, we had to "put on the user's hat." We
                  then created a user persona to help us understand the problems
                  that the user will face. We had two personas for this: Kenneth
                  and Celine.`}
                />
                <div className="row mb-4">
                  <div className="col-md-6">
                    <img
                      src={userStudyOne}
                      className="img-fluid"
                      loading="lazy"
                    />
                  </div>
                  <div className="col-md-6">
                    <img
                      src={userStudyTwo}
                      className="img-fluid"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>

              <br />

              {/** Color palette and color */}
              <div className="my-4">
                <SubHeaderWithLineAndArrow
                  description={
                    "2.2. Determining the moodboard and color palettes 🎨"
                  }
                />

                <SubHeaderDescription
                  paragraph={`When considering our colour scheme and palette, we wanted
                  something that was soothing to look at and not too piercing to
                  look at. We wanted our app to invoke the right emotion as
                  different colours can mean different things. The choice of
                  wanting to go with a more mature app meant that a more muted
                  colour scheme was chosen. `}
                />

                <br />

                <img
                  src={colorPalatte}
                  className="img-fluid h-100"
                  loading="lazy"
                />
              </div>

              <br />

              {/** Flow */}
              <div className="my-4">
                <SubHeaderWithLineAndArrow
                  description={"2.3. Going with the flow 🌊"}
                />
                <SubHeaderDescription
                  paragraph={`This is a simple flow diagram, where the screens are pages or
                  destinations, and diamonds are decision points.`}
                />
                <br />
                <img
                  src={flowDiagram}
                  className="img-fluid h-100"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="section the-end">
              {/** Title */}
              <PrimaryHeaderWithLine
                title={`3. Creating and Finishing the app`}
              />

              <SubHeaderDescription
                paragraph={
                  <>
                    Following user research, we decided to build the app in
                    Android, Java. We also decided to create the app using the
                    Material Design guidelines. Additional features include
                    using FireBase Realtime Database and including payment
                    methods for premium versions.
                  </>
                }
              />

              <div className="row">
                <div className="col-md-4">
                  <img
                    src={userStudySix}
                    className="d-block mx-lg-auto img-fluid"
                    loading="lazy"
                  />
                </div>
                <div className="col-md-4">
                  <img
                    src={userStudyFive}
                    className="d-block mx-lg-auto img-fluid"
                    loading="lazy"
                  />
                </div>
                <div className="col-md-4">
                  <img
                    src={userStudySeven}
                    className="d-block mx-lg-auto img-fluid"
                    loading="lazy"
                  />
                </div>
              </div>

              <SubHeaderDescription
                paragraph={
                  <>
                    <br />
                    <br />
                    The app was entirely created in the Android Studio IDE, and
                    the app repository can be found on{" "}
                    <a href="https://github.com/Ducksss/Daybreak">
                      my GitHub Account
                    </a>
                    .
                  </>
                }
              />

              {/** Content */}
            </div>

            {/* <article className="blog-post">
              <h2 className="blog-post-title">Another blog post</h2>
              <p className="blog-post-meta">
                December 23, 2020 by <a href="#">Jacob</a>
              </p>

              <p>
                This is some additional paragraph placeholder content. It has
                been written to fill the available space and show how a longer
                snippet of text affects the surrounding content. We'll repeat it
                often to keep the demonstration flowing, so be on the lookout
                for this exact same string of text.
              </p>
              <blockquote>
                <p>
                  Longer quote goes here, maybe with some{" "}
                  <strong>emphasized text</strong> in the middle of it.
                </p>
              </blockquote>
              <p>
                This is some additional paragraph placeholder content. It has
                been written to fill the available space and show how a longer
                snippet of text affects the surrounding content. We'll repeat it
                often to keep the demonstration flowing, so be on the lookout
                for this exact same string of text.
              </p>
              <h3>Example table</h3>
              <p>And don't forget about tables in these posts:</p>
              

              <p>
                This is some additional paragraph placeholder content. It's a
                slightly shorter version of the other highly repetitive body
                text used throughout.
              </p>
            </article> */}

            {/* <article className="blog-post">
              <h2 className="blog-post-title">New feature</h2>
              <p className="blog-post-meta">
                December 14, 2020 by <a href="#">Chris</a>
              </p>

              <p>
                This is some additional paragraph placeholder content. It has
                been written to fill the available space and show how a longer
                snippet of text affects the surrounding content. We'll repeat it
                often to keep the demonstration flowing, so be on the lookout
                for this exact same string of text.
              </p>
              <ul>
                <li>First list item</li>
                <li>Second list item with a longer description</li>
                <li>Third list item to close it out</li>
              </ul>
              <p>
                This is some additional paragraph placeholder content. It's a
                slightly shorter version of the other highly repetitive body
                text used throughout.
              </p>
            </article>

            <nav className="blog-pagination" aria-label="Pagination">
              <a className="btn btn-outline-primary" href="#">
                Older
              </a>
              <a
                className="btn btn-outline-secondary disabled"
                href="#"
                tabindex="-1"
                aria-disabled="true"
              >
                Newer
              </a>
            </nav> */}
          </div>
        </div>
      </div>
      <Footer />
    </AnimationRevealPage>
  );
};

export default Daybreak;
